// ============================================================
//  TabProvider — Context Provider for Dynamic Tab Management
// ============================================================

import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { useTabManager } from '../hooks/useTabManager';
import { DynamicTab } from '../types/tabs';

interface TabContextValue {
  // Tab state
  tabs: DynamicTab[];
  activeTab: string;
  editing: string | null;
  draggedTab: string | null;
  isInitialized: boolean;

  // Tab actions
  addTab: (label: string, icon?: string) => void;
  updateTab: (id: string, updates: Partial<DynamicTab>) => void;
  updateTabNoSave: (id: string, updates: Partial<DynamicTab>) => void; // New: update without automatic save
  deleteTab: (id: string) => void;
  setTabs: (tabs: DynamicTab[]) => void;
  reorderTabs: (fromIndex: number, toIndex: number) => void;
  setActiveTab: (id: string) => void;
  setEditingTab: (id: string | null) => void;
  setDraggedTab: (id: string | null) => void;

  // Tab helpers
  getActiveTab: () => DynamicTab | undefined;
  getTabBySlug: (slug: string) => DynamicTab | undefined;
  canDeleteTab: (id: string) => boolean;
  canAddTab: () => boolean;

  // Legacy compatibility
  getLegacyPageId: (tabId: string) => string;
  getTabFromLegacyPageId: (pageId: string) => DynamicTab | undefined;

  // Save callback
  onTabUpdate?: () => void;
  triggerSave: () => void; // New: manual save trigger

  getTabs: () => DynamicTab[]; // New: get current tabs list
}

const TabContext = createContext<TabContextValue | null>(null);

interface TabProviderProps {
  children: React.ReactNode;
  initialTabs?: DynamicTab[];
  onTabUpdate?: () => void;
}

export function TabProvider({
  children,
  initialTabs,
  onTabUpdate,
}: TabProviderProps): React.ReactElement {
  const tabManager = useTabManager(initialTabs);

  // Wrap updateTab to call onTabUpdate after updating
  const updateTabWithSave = useCallback(
    (id: string, updates: Partial<DynamicTab>) => {
      tabManager.updateTab(id, updates);
      onTabUpdate?.();
    },
    [tabManager.updateTab, onTabUpdate]
  );

  // Wrap addTab to call onTabUpdate after adding
  const addTabWithSave = useCallback(
    (label: string, icon?: string) => {
      tabManager.addTab(label);
      onTabUpdate?.();
    },
    [tabManager.addTab, onTabUpdate]
  );

  // Wrap deleteTab to call onTabUpdate after deleting
  const deleteTabWithSave = useCallback(
    (id: string) => {
      tabManager.deleteTab(id);
      onTabUpdate?.();
    },
    [tabManager.deleteTab, onTabUpdate]
  );

  // Manual save trigger
  const triggerSave = useCallback(() => {
    onTabUpdate?.();
  }, [onTabUpdate]);

  // Legacy compatibility functions
  const getLegacyPageId = (tabId: string): string => {
    // Since id and slug are now the same, just return the tabId directly
    return tabId;
  };

  const getTabFromLegacyPageId = (pageId: string): DynamicTab | undefined => {
    // Since id and slug are now the same, just find by id
    return tabManager.tabs.find((t: any) => t.id === pageId);
  };

  const contextValue = useMemo(
    (): TabContextValue => ({
      ...tabManager,
      updateTab: updateTabWithSave, // Automatic save version
      updateTabNoSave: tabManager.updateTab, // Manual save version (no automatic trigger)
      addTab: addTabWithSave,
      deleteTab: deleteTabWithSave,
      getLegacyPageId,
      getTabFromLegacyPageId,
      onTabUpdate,
      triggerSave,
    }),
    [
      tabManager,
      updateTabWithSave,
      addTabWithSave,
      deleteTabWithSave,
      onTabUpdate,
      triggerSave,
    ]
  );

  return (
    <TabContext.Provider value={contextValue}>{children}</TabContext.Provider>
  );
}

// Hook to use tab context
export function useTabContext(): TabContextValue {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('useTabContext must be used within a TabProvider');
  }
  return context;
}

// Hook for backward compatibility with legacy PageId system
export function useLegacyPageSupport(): any {
  const tabContext = useTabContext();

  const getCurrentPageId = (): string => {
    return tabContext.getLegacyPageId(tabContext.activeTab);
  };

  const switchToPageId = (pageId: string): void => {
    const tab = tabContext.getTabFromLegacyPageId(pageId);
    if (tab) {
      tabContext.setActiveTab(tab.id);
    }
  };

  const getPageLabel = (pageId: string): string => {
    const tab = tabContext.getTabFromLegacyPageId(pageId);
    return tab?.label ?? pageId;
  };

  return {
    currentPageId: getCurrentPageId(),
    switchToPageId,
    getPageLabel,
    tabs: tabContext.getTabs(), // Expose current tabs list
    activeTab: tabContext.activeTab,
    // Expose tab management functions
    setActiveTab: tabContext.setActiveTab,
    setTabs: tabContext.setTabs,
    isInitialized: tabContext.isInitialized,
  };
}
